import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import React from 'react';

import { BidConsoleOpener } from '@/components/BidConsoleOpener/BidConsoleOpener';
import CatalogDate from '@/components/CatalogDate/CatalogDate';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getLiveCatalogCurrentItemIndex } from '@/redux/modules/console';
import {
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryIsCatalogOnly,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummaryIsTimedPlusAuction,
    getItemSummarySaleStartTs,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { ItemCardNavigationTrigger } from '@/utils/amplitude/analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

export type ItemCardShortDateProps = {
    allowBidding?: boolean;
    className?: string;
    forceDate?: boolean;
    hasNewSimilarItemsIconCTA?: boolean;
    itemId: number;
    // Here for disabling hover styles on parent when component is an active one
    onMouseEnter?: React.MouseEventHandler<HTMLSpanElement>;
    onMouseLeave?: React.MouseEventHandler<HTMLSpanElement>;
    onNavigate?: (trigger: ItemCardNavigationTrigger) => void;
    showDateWithoutYear?: boolean;
    // Opt-in, and it switches two labels together: "Live Now - N Lots Away" while live and "Starts in X" before the sale.
    // Other surfaces keep their own live-status and countdown labels.
    showLiveLotPosition?: boolean;
    showOnlyYear?: boolean;
    usePrimary?: boolean;
};

/**
 * This component will either return a short format date, or the text "Available to Buy Now"
 * if the item is available to buy now. If it can be bid on now, show that instead
 */
export default function ItemCardShortDate({
    allowBidding = false,
    className,
    forceDate = false,
    hasNewSimilarItemsIconCTA = false,
    itemId,
    onMouseEnter,
    onMouseLeave,
    onNavigate,
    showDateWithoutYear = false,
    showLiveLotPosition = false,
    showOnlyYear = false,
    usePrimary = false,
}: ItemCardShortDateProps) {
    const formatMessage = useAppFormatMessage();
    const isMobile = useIsMobileLayout();
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isTimedPlusAuction = useAppSelector((state) => getItemSummaryIsTimedPlusAuction(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));

    const isDone = catalogStatus === 'done';
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const lotsAway = index - currentItemIndex;

    if (isTimedPlusAuction && !forceDate) {
        return null;
    }

    if (Date.now() > saleStartTs * 1000 && !isSold && !isPassed && !isCatalogOnly) {
        const showCombinedLotPosition = showLiveLotPosition && !isMobile && lotsAway > 0;
        const showLotPosition = lotsAway > 0 && allowBidding;

        let liveStatusMessage = formatMessage(['itemBidding', 'liveNow']);
        if (showCombinedLotPosition) {
            liveStatusMessage =
                lotsAway === 1
                    ? formatMessage(['itemBidding', 'liveNowLotAway_short'])
                    : formatMessage(['itemBidding', 'liveNowLotsAway_short'], { values: { lotsAway } });
        } else if (showLotPosition) {
            liveStatusMessage =
                lotsAway === 1
                    ? formatMessage(['itemBidding', 'lotAway_short'])
                    : formatMessage(['itemBidding', 'lotsAway_short'], { values: { lotsAway } });
        }

        return (
            <BidConsoleOpener
                catalogId={catalogId}
                onNavigate={() => onNavigate?.(ItemCardNavigationTrigger.BidLiveClick)}
            >
                <BodySecondary
                    className={cn(
                        'inline-flex !min-h-0 items-center !py-0 align-middle text-xs leading-[18px]',
                        className
                    )}
                    color="critical"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    role="button"
                >
                    {liveStatusMessage}
                </BodySecondary>
            </BidConsoleOpener>
        );
    }

    if (buyNowStatus === BuyNowStatus.Available && isDone) {
        return (
            <BodySecondary
                className={className}
                color="critical"
                key={itemId}
            >
                {formatMessage(['available_to_buy_now'])}
            </BodySecondary>
        );
    }

    return (
        <BodySecondary
            className={className}
            key={itemId}
        >
            <CatalogDate
                className={className}
                date={saleStartTs}
                hasNewSimilarItemsIconCTA={hasNewSimilarItemsIconCTA}
                showDateWithoutYear={showDateWithoutYear}
                showOnlyYear={showOnlyYear}
                showStartsInLabel={showLiveLotPosition}
                usePrimary={usePrimary}
            />
        </BodySecondary>
    );
}
